import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Gavel, Calculator, Scale, CheckCircle2 } from "lucide-react";
import { Header } from "../sections/Header";
import { Footer } from "../sections/Footer";
import { StickyCTA } from "../sections/StickyCTA";
import { FIRM, PRACTICE_AREA_CATEGORIES } from "../data/mock";
import {
  Seo,
  buildBreadcrumbSchema,
  buildWebPageSchema,
  ORGANIZATION_SCHEMA,
} from "../components/Seo";

const ICONS = { Gavel, Calculator, Scale };

const PracticeAreas = () => {
  // Scroll to top on mount, but honor hash anchors
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo
        title="Forensic Accounting Legal Services Texas — Practice Areas"
        description="Three practice areas at The Bufkin Law Firm: business and financial litigation, forensic accounting consulting & testifying expert services, and non-litigation transactional, estate and probate work. Texas attorney with 50+ years of experience."
        path="/practice-areas"
        keywords="Texas business litigation, forensic accounting expert witness, bankruptcy trustee attorney, estate planning probate Texas, financial consulting attorney"
        schemas={[
          ORGANIZATION_SCHEMA,
          buildWebPageSchema({
            title: "Practice Areas | The Bufkin Law Firm",
            description: "Litigation, financial consulting & expert witness, and non-litigation legal services.",
            path: "/practice-areas",
          }),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Practice Areas", path: "/practice-areas" },
          ]),
        ]}
      />
      <main data-testid="practice-areas-page" className="bg-ivory min-h-screen">
      <Header />

      {/* Hero */}
      <section
        data-testid="practice-hero"
        className="relative pt-36 lg:pt-44 pb-20 lg:pb-28 bg-ivory overflow-hidden"
      >
        {/* Background dots */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #102845 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>
        <div className="absolute top-20 -right-40 w-[500px] h-[500px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-up">
            <div className="gold-line" />
            <span
              data-testid="practice-eyebrow"
              className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium"
            >
              Services · Practice Area Overview
            </span>
          </div>
          <h1
            data-testid="practice-headline"
            className="font-serif text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-navy leading-[1.02] tracking-tight max-w-5xl fade-up-delay-1"
          >
            Our <span className="italic text-gold">practices.</span>
          </h1>
          <p
            data-testid="practice-subhead"
            className="mt-8 text-lg lg:text-xl text-navy/75 max-w-3xl leading-relaxed fade-up-delay-2"
          >
            Three pillars, one approach: every engagement is handled with the
            combined skill set of a trial lawyer and a Certified Public
            Accountant. Below is an overview of the matters Dick Bufkin
            regularly handles for clients and counsel across Texas.
          </p>

          {/* Quick anchor nav */}
          <nav
            data-testid="practice-anchor-nav"
            className="mt-12 flex flex-wrap gap-3 fade-up-delay-3"
          >
            {PRACTICE_AREA_CATEGORIES.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                data-testid={`anchor-${cat.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-[0.18em] uppercase font-medium text-navy bg-white border border-[#C0974E]/30 hover:border-gold hover:bg-gold/5 transition-all"
              >
                {cat.title.length > 30 ? cat.title.split(" ").slice(0, 3).join(" ") + "…" : cat.title}
              </a>
            ))}
          </nav>
        </div>
      </section>

      {/* Practice categories */}
      {PRACTICE_AREA_CATEGORIES.map((category, idx) => {
        const Icon = ICONS[category.icon] || Scale;
        const isDark = idx === 1; // Middle section gets dark treatment for rhythm
        return (
          <section
            key={category.id}
            id={category.id}
            data-testid={`practice-section-${category.id}`}
            className={`py-24 lg:py-32 relative scroll-mt-24 ${
              isDark ? "bg-navy text-ivory" : idx === 2 ? "bg-cream" : "bg-ivory"
            }`}
          >
            {isDark && (
              <>
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
              </>
            )}

            <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
              {/* Left: Heading */}
              <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
                <div className="flex items-center gap-3 mb-6">
                  <div className="gold-line" />
                  <span
                    className={`text-xs tracking-[0.3em] uppercase font-medium ${
                      isDark ? "text-gold" : "text-navy/70"
                    }`}
                  >
                    {category.eyebrow}
                  </span>
                </div>

                <div
                  className={`w-14 h-14 rounded-sm flex items-center justify-center mb-8 ${
                    isDark ? "bg-gold/15" : "bg-navy/5"
                  }`}
                >
                  <Icon
                    className={`w-6 h-6 ${isDark ? "text-gold" : "text-navy"}`}
                    strokeWidth={1.25}
                  />
                </div>

                <h2
                  data-testid={`practice-title-${category.id}`}
                  className={`font-serif text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.05] tracking-tight ${
                    isDark ? "text-ivory" : "text-navy"
                  }`}
                >
                  {category.title}
                </h2>

                <p
                  className={`mt-6 text-lg leading-snug font-serif italic ${
                    isDark ? "text-gold-light" : "text-gold"
                  }`}
                >
                  {category.tagline}
                </p>

                <p
                  className={`mt-6 text-base lg:text-lg leading-relaxed max-w-md ${
                    isDark ? "text-ivory/75" : "text-navy/75"
                  }`}
                >
                  {category.description}
                </p>
              </div>

              {/* Right: Items list */}
              <div className="lg:col-span-7">
                <div
                  className={`text-xs uppercase tracking-[0.2em] mb-6 ${
                    isDark ? "text-gold" : "text-navy/60"
                  }`}
                >
                  Representative Engagements
                </div>
                <ul className="space-y-px">
                  {category.items.map((item, itemIdx) => (
                    <li
                      key={itemIdx}
                      data-testid={`practice-item-${category.id}-${itemIdx}`}
                      className={`group flex items-start gap-5 py-5 lg:py-6 border-b transition-colors ${
                        isDark
                          ? "border-ivory/10 hover:border-gold/40"
                          : "border-[#C0974E]/15 hover:border-gold/50"
                      }`}
                    >
                      <div className="pt-1.5 flex-shrink-0">
                        <CheckCircle2
                          className={`w-4 h-4 ${
                            isDark ? "text-gold" : "text-gold"
                          }`}
                          strokeWidth={1.5}
                        />
                      </div>
                      <div
                        className={`text-base lg:text-lg font-serif leading-snug pt-0.5 ${
                          isDark ? "text-ivory/90" : "text-navy"
                        }`}
                      >
                        {item}
                      </div>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/practice-areas/${category.slug}`}
                  data-testid={`learn-more-${category.id}`}
                  className={`inline-flex items-center gap-3 mt-10 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium transition-all group ${
                    isDark
                      ? "border border-gold text-gold hover:bg-gold hover:text-navy-deep"
                      : "btn-outline"
                  }`}
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA strip */}
      <section
        data-testid="practice-cta"
        className="py-20 lg:py-28 bg-ivory relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #102845 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-3 mb-6 justify-center">
            <div className="gold-line" />
            <span className="text-xs tracking-[0.3em] uppercase text-navy/70 font-medium">
              Start the Conversation
            </span>
            <div className="gold-line" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] tracking-tight">
            Not sure which category
            <br />
            your matter <span className="italic text-gold">falls under?</span>
          </h2>
          <p className="mt-8 text-base lg:text-lg text-navy/75 max-w-2xl mx-auto leading-relaxed">
            Most aren't — and that's the point. The work cuts across litigation,
            forensic accounting and planning. Send Dick a few sentences about
            your situation and he'll tell you straight whether he can help.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="/#contact"
              data-testid="cta-primary"
              className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-xs tracking-[0.25em] uppercase font-medium group"
            >
              Schedule a Consultation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={`tel:${FIRM.phone.replace(/[^\d+]/g, "")}`}
              data-testid="cta-phone"
              className="inline-flex items-center gap-3 px-2 py-4 text-sm text-navy font-medium hover:text-gold transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              <span className="link-underline">{FIRM.phone}</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <StickyCTA />
    </main>
    </>
  );
};

export default PracticeAreas;
